import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../shared/ToastNotification';
import { getAPIRoot } from '../../services/api';
import { Loader2, X } from 'lucide-react';

const CONTEXT_COPY = {
  checkin: {
    headline: "Log your night",
    subtitle: "Check-ins unlock VIP status & FridayStag Pass priority.",
    hook: "First check-in unlocks your STAGSTER Pass"
  },
  fastlane: {
    headline: "Skip the line",
    subtitle: "Verified members get priority entry and door-ready intel.",
    hook: "FastLane entry is live at partner venues"
  },
  explore: {
    headline: "You\u2019re almost there",
    subtitle:
      "Sign in with Google, then a short setup. After that, see spend, seating, and solo notes on bars near you.",
    hook: "Join the first members in your city",
  },
  work: {
    headline: "Find your spot",
    subtitle: "Wi-Fi speeds, plug points, and vibe - all verified.",
    hook: "Work cafés mapped city-by-city"
  },
  fswipe: {
    headline: "Meet someone tonight",
    subtitle: "Real people. Verified profiles. Every Friday.",
    hook: "FridaySwipe drops at 7 PM"
  },
  default: {
    headline: 'Join STAGSTER',
    subtitle: 'Sign in with Google, then a quick profile for your city. Usually under a minute. No extra passwords.',
    hook: 'Join the first members in your city',
  },
};

const getCopy = (origin) => {
  if (!origin) return CONTEXT_COPY.default;
  return CONTEXT_COPY[origin] || CONTEXT_COPY.default;
};

/** Real social-proof numbers from GET /api/meta/stats, cached briefly so the modal doesn't hammer the API. */
const STATS_CACHE_TTL_MS = 60_000;
let statsCache = { at: 0, data: null };

async function fetchStats() {
  if (Date.now() - statsCache.at < STATS_CACHE_TTL_MS) return statsCache.data;
  try {
    const res = await fetch(`${getAPIRoot()}/api/meta/stats`, {
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) return null;
    const data = await res.json();
    statsCache = { at: Date.now(), data: data?.ok ? data : null };
    return statsCache.data;
  } catch {
    return null;
  }
}

/**
 * Hook copy per context. Uses real counts when they're >= 2 (a "1+ members"
 * hook reads as broken), otherwise falls back to honest non-numeric copy.
 */
function buildHook(origin, stats) {
  if (origin === 'fswipe') return CONTEXT_COPY.fswipe.hook;
  const fallback = getCopy(origin).hook;
  if (!stats) return fallback;

  const fmt = (value) =>
    typeof value === 'number' && value >= 2
      ? value.toLocaleString('en-IN')
      : null;

  switch (origin) {
    case 'checkin': {
      const v = fmt(stats.nights_logged_this_month);
      return v ? `${v}+ nights logged this month` : fallback;
    }
    case 'fastlane': {
      const v = fmt(stats.fastlane_venue_count);
      return v ? `FastLane active at ${v} venues tonight` : fallback;
    }
    case 'work': {
      const v = fmt(stats.work_cafe_count);
      return v ? `${v}+ work-friendly cafés mapped` : fallback;
    }
    case 'explore':
    case 'default': {
      const v = fmt(stats.member_count);
      return v ? `Join ${v}+ solo nightlifers` : fallback;
    }
    default:
      return fallback;
  }
}

const AuthPrompt = ({ open, onOpenChange, origin, blur = true, allowClose = true }) => {
  const { signInWithGoogle } = useAuth();
  const { showError } = useToast();
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);

  const copy = useMemo(() => getCopy(origin), [origin]);
  const hook = useMemo(() => buildHook(origin, stats), [origin, stats]);

  useEffect(() => {
    let cancelled = false;
    fetchStats().then((data) => {
      if (!cancelled) setStats(data);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!open) setLoading(false);
  }, [open]);

  const handleClose = useCallback((next) => {
    if (!allowClose && next === false) return;
    if (typeof onOpenChange === 'function') onOpenChange(next);
  }, [onOpenChange, allowClose]);

  const handleGoogle = useCallback(async () => {
    if (typeof signInWithGoogle !== 'function') return;
    try {
      setLoading(true);
      await signInWithGoogle();
    } catch (error) {
      showError?.(error?.message || 'Sign-in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [signInWithGoogle, showError]);

  return (
    <Dialog open={open} onOpenChange={allowClose ? handleClose : undefined}>
      <DialogContent
        allowClose={false}
        className="auth-prompt overflow-hidden p-0 sm:max-w-[380px] w-[92vw] mx-auto rounded-[28px] border-0 bg-transparent shadow-none"
      >
        {/* Electric glowing shell */}
        <div className="relative rounded-[28px] overflow-hidden">
          {/* Animated red border glow */}
          <div className="auth-glow-border absolute -inset-[1px] rounded-[28px] opacity-75 blur-[2px]"
            style={{ background: 'conic-gradient(from var(--angle), #ff2774, #ff6b35, #ff2774, #c0003c, #ff2774)' }}
          />
          {/* Solid border ring on top */}
          <div className="absolute inset-0 rounded-[28px] border border-[#ff2774]/50 z-[1]" />

          {/* Content panel */}
          <div className="relative z-[2] bg-[#070709] rounded-[27px] overflow-hidden">

            {/* Top glow splash */}
            <div className="pointer-events-none absolute inset-x-0 -top-24 h-56 bg-[radial-gradient(ellipse_70%_60%_at_50%_0%,rgba(255,39,116,0.22),transparent_70%)]" />

            <div className="relative flex flex-col items-center px-6 pt-8 pb-5">
              {allowClose && (
                <button
                  type="button"
                  onClick={() => handleClose(false)}
                  className="absolute right-4 top-4 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-white/[0.05] text-white/40 transition-all hover:bg-white/[0.1] hover:text-white active:scale-95"
                  aria-label="Close"
                >
                  <X className="h-4 w-4" strokeWidth={2} />
                </button>
              )}

              {/* Icon */}
              <div className="relative mb-5">
                <div className="absolute inset-0 rounded-2xl bg-[#ff2774]/30 blur-2xl scale-150" />
                <div className="relative flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-b from-[#1e1c21] to-[#111013] ring-1 ring-white/10 shadow-2xl overflow-hidden">
                  <img src="/logo-mascot.webp" alt="STAGSTER" className="w-10 h-10 object-contain" />
                </div>
              </div>

              <DialogHeader className="items-center text-center space-y-2">
                <DialogTitle className="text-[24px] font-black tracking-tight text-white leading-tight">
                  {copy.headline}
                </DialogTitle>
                <DialogDescription className="text-[14px] leading-relaxed text-white/55 max-w-[84%] mx-auto">
                  {copy.subtitle}
                </DialogDescription>
              </DialogHeader>

              {/* Live pill */}
              <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-white/[0.07] bg-white/[0.04] px-3.5 py-1.5">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                </span>
                <span className="text-[11px] font-semibold tracking-wide text-white/60 uppercase">{hook}</span>
              </div>
            </div>

            {/* Action area */}
            <div className="px-5 pb-6 pt-4 space-y-3 border-t border-white/[0.05]">
              {/* Google button */}
              <button
                type="button"
                onClick={handleGoogle}
                disabled={loading}
                className="group relative flex w-full items-center justify-center gap-3 overflow-hidden rounded-2xl bg-white px-4 py-[14px] text-center transition-all duration-200 hover:bg-gray-50 hover:shadow-[0_0_28px_rgba(255,255,255,0.2)] active:scale-[0.97] disabled:opacity-60 disabled:cursor-not-allowed shadow-[0_4px_24px_rgba(255,255,255,0.12)]"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin text-black" />
                ) : (
                  <img src="/g-logo.png" alt="Google" className="h-5 w-5" />
                )}
                <span className="text-[15px] font-bold tracking-tight text-black">
                  {loading ? 'Redirecting…' : 'Sign in with Google'}
                </span>
                {/* Shimmer */}
                <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/20 to-transparent group-hover:translate-x-full transition-transform duration-700 ease-in-out" />
              </button>

              {allowClose && (
                <button
                  type="button"
                  onClick={() => handleClose(false)}
                  className="w-full text-[12px] font-semibold text-white/30 transition-colors hover:text-white/60 uppercase tracking-widest py-1"
                >
                  Maybe later
                </button>
              )}

              <p className="text-center text-[10px] leading-relaxed text-white/20 px-2">
                By continuing, you agree to our{' '}
                <a href="/terms" className="text-white/35 underline hover:text-white/60" onClick={() => handleClose(false)}>Terms</a>
                {' & '}
                <a href="/privacy" className="text-white/35 underline hover:text-white/60" onClick={() => handleClose(false)}>Privacy Policy</a>.
              </p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AuthPrompt;
